import type { AccessQueueItemPublic, ItEmailPublic } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import {
  ApiError,
  createItEmail,
  markItEmailSent,
  takeBackItEmail,
  updateItEmailText,
} from '../../../../lib/api';
import { formatDateTime } from '../../../../lib/format';
import { queryKeys } from '../../../../lib/queryKeys';
import { showToast } from '../../../../lib/toast';

import { type ItEmailSummary, ItEmailDialog } from './ItEmailDialog';
import { ItPickDialog } from './ItPickDialog';

/** Where the flow opens: the picker (with the Queue's ticks), or an email that exists. */
export type ItFlowStart =
  | { kind: 'pick'; initial: ReadonlySet<string> }
  | { kind: 'email'; email: ItEmailPublic };

type Step =
  | { kind: 'pick'; initial: ReadonlySet<string> }
  /** `fresh`: drafted in this flow and never saved — Cancel retires it, as the demo drops it. */
  | { kind: 'email'; email: ItEmailPublic; fresh: boolean };

function summaryOf(
  email: ItEmailPublic,
  queueById: ReadonlyMap<string, AccessQueueItemPublic>,
): ItEmailSummary {
  const live = email.items.filter((item) => item.status !== 'taken_back');
  const setUp = live.filter((item) => item.action === 'set_up').length;
  return {
    items: live.length,
    systems: new Set(live.map((item) => item.system.id)).size,
    setUp,
    remove: live.length - setUp,
    shared: live.filter((item) => (queueById.get(item.recordId)?.sharedWith.length ?? 0) > 0).length,
  };
}

/**
 * NEW EMAIL TO IT, end to end (spec row 15; FINAL demo change 1). Step 1 picks the work;
 * the server then checks every record, decides Set up or Remove and writes the first text,
 * grouped by system and person ("rotate, don't delete" on shared logins is the server's
 * wording). Step 2 shows it in colour, lets the person edit, save, copy and Mark as sent.
 * ACCOUNTS SENDS NOTHING TO IT: the person copies the text into their own mail app.
 */
export function ItEmailFlow({
  start,
  pickable,
  queueItems,
  onClose,
}: {
  start: ItFlowStart;
  /** Open IT work in no live email — the picker's rows. */
  pickable: readonly AccessQueueItemPublic[];
  /** The whole role-scoped Queue, to say which items are on a shared login. */
  queueItems: readonly AccessQueueItemPublic[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(() =>
    start.kind === 'pick' ? start : { kind: 'email', email: start.email, fresh: false },
  );
  const [error, setError] = useState<string | null>(null);

  const queueById = new Map(queueItems.map((item) => [item.id, item] as const));
  const failed = (cause: unknown) => {
    setError(cause instanceof ApiError ? cause.message : STR.errors.generic);
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
  };

  const draft = useMutation({
    mutationFn: (picked: readonly AccessQueueItemPublic[]) =>
      createItEmail(picked.map((item) => item.id)),
    onSuccess: ({ email }) => {
      setError(null);
      setStep({ kind: 'email', email, fresh: true });
    },
    onError: failed,
    onSettled: refresh,
  });

  const save = useMutation({
    mutationFn: ({ email, text }: { email: ItEmailPublic; text: string }) =>
      updateItEmailText(email.id, text),
    onSuccess: () => {
      showToast(STR.itEmails.savedDraft);
      onClose();
    },
    onError: failed,
    onSettled: refresh,
  });

  const send = useMutation({
    mutationFn: async ({ email, text }: { email: ItEmailPublic; text: string }) => {
      // The text as it is on screen is what was sent: save an edit first, then mark it sent.
      if (text !== email.text) await updateItEmailText(email.id, text);
      return markItEmailSent(email.id);
    },
    onSuccess: ({ email }) => {
      showToast(STR.itEmails.markedSent(email.openItemCount));
      onClose();
    },
    onError: failed,
    onSettled: refresh,
  });

  const discard = useMutation({
    mutationFn: (email: ItEmailPublic) => takeBackItEmail(email.id),
    onError: () => {
      showToast(STR.errors.generic, 'warn');
    },
    onSettled: refresh,
  });

  if (step.kind === 'pick') {
    return (
      <ItPickDialog
        items={pickable}
        initial={step.initial}
        pending={draft.isPending}
        error={error}
        onCancel={onClose}
        onDraft={(picked) => {
          draft.mutate(picked);
        }}
      />
    );
  }

  const email = step.email;
  const sent = email.status !== 'draft';
  const by = email.sentBy?.name ?? email.createdBy.name;
  return (
    <ItEmailDialog
      key={email.id}
      initialText={email.text}
      summary={summaryOf(email, queueById)}
      sentLine={
        sent
          ? STR.itEmails.sentLine(formatDateTime(email.sentAt ?? email.createdAt), by, email.openItemCount)
          : null
      }
      pending={save.isPending || send.isPending}
      error={error}
      onCancel={() => {
        if (step.fresh) discard.mutate(email);
        onClose();
      }}
      onSave={(text) => {
        save.mutate({ email, text });
      }}
      onMarkSent={(text) => {
        send.mutate({ email, text });
      }}
    />
  );
}
